/*
 * @providesModule CircleProgressLevel
 *
 */

import _ from 'lodash';
import * as Animatable from 'react-native-animatable';
import Label from 'Label';
import React from 'react';
import SkillzImage from 'SkillzImage';
import Optional from 'Optional';
import SkillzStyle, { addColors } from 'SkillzStyle';
import SKZConstants from 'SKZConstants';
import SKZPlayerLevel from 'SKZPlayerLevel';
import styles from 'CircleProgressLevelStyle';
import {View, Easing, Platform} from 'react-native';
import AnimatedCircularProgressWithGradient from 'AnimatedCircularProgress';

const DEFAULT_SIZE = 136;
const DEFAULT_INITIAL_PREFILL = 0;
const DEFAULT_FINAL_FILL = 100;
const DEFAULT_ANIMATION_STARTING_POINT = 180;
const PROGRESS_BAR_WIDTH = 6;
const SIZE_INNER_CIRCLE = 0.94;
const SIZE_LEVEL_CIRCLE = 0.375;
const Y_OFFSET = 0.22;
const SIZE_LEVEL_TEXT = 0.6;
const BAR_DURATION = 650;
const BAR_DURATION_WITH_GRADIENT = 2000;
const NUMBER_DURATION = 500;

const LIGHT_GRAY = '#ebebeb';
const DARK_GRAY = '#999797';

// Precalculate all the color math
// eslint-disable-next-line no-magic-numbers
const BACKGROUND_COLOR = addColors(
  SkillzStyle.convertToOpacity(SkillzStyle.fullStyles.viewPrimaryColor.backgroundColor, 0.75),
  'rgba(0,0,0,0.6)'
);

type ICircleProgressLevelProps = {
  gameLevelData: SKZPlayerLevel;
  size?: number; //size of the whole component
  backgroundColor?: string; //color of the meter that's not yet filled by xp
  rotation?: number; //where the animation will start
  isLevelUpModal?: boolean;
  placeholder?: boolean; // placeholder behavior changes slightly based on if gameLevelData is provided
  levelCircleImage?: string; // replaces the standard level circle
  renderCenterImage?: (height: number, width: number) => React.ReactNode; // overrides current center rendering logic. takes height and width parameters.
  playConfetti?: () => void;
  animated?: boolean;
  sizeOfImageMult?: number;
  testId?: string;
  levelTestID?: string;
  level?: {
    name: string;
    image: string;
  };
  backgroundColorInnerCircle?: string; // overrides the standard inner circle background color (currently just used for circle behind updated Skillz logo)
  innerCircleBorderColor?: string; // overrides the standard inner circle border color (currently just used for circle behind updated Skillz logo)
  isNewTierDetails?: boolean; // if true, the progress circle will be rendered with gradient colors and percentage of filled Progress text
  currentTierImage?: string; // this is the image for the current tier inside circularProgressBar
  beginColor?: string; // this is the color code for the current tier
  endColor?: string; // this is the color code for the next tier
  tierProgress: number; // this is the progress of the current tier
};

type ICircleProgressLevelState = {
  fill: number;
  tintColor: string;
  rotation: number;
  currentLevel: number;
  didLevelUp: boolean;
};

export default class CircleProgressLevel extends React.Component<
  ICircleProgressLevelProps,
  ICircleProgressLevelState
> {
  static defaultProps = {
    animated: true,
    isLevelUpModal: false,
  };

  size: number;
  fillPercentage: number;
  prefill: any;
  backgroundColor: string;
  lastSeenLevel: number;
  currentLevel: number;

  constructor(props: ICircleProgressLevelProps) {
    super(props);

    this.size = this.props.size ? this.props.size : DEFAULT_SIZE;
    this.fillPercentage = this._createFillPercentageBasedOnUserLevelData();
    this.prefill = this.props.animated ? DEFAULT_INITIAL_PREFILL : this.fillPercentage;
    this.backgroundColor = this.props.isLevelUpModal ? LIGHT_GRAY : BACKGROUND_COLOR;
    this.lastSeenLevel = _.get(this.props.gameLevelData, 'last_seen_level');
    this.currentLevel = _.get(this.props.gameLevelData, 'current_level');

    this.state = {
      fill: this.fillPercentage,
      tintColor: SkillzStyle.fullStyles.tabHighlightColor.color,
      rotation: this.props.rotation ? this.props.rotation : DEFAULT_ANIMATION_STARTING_POINT,
      currentLevel: (this.props.gameLevelData && this.props.gameLevelData.last_seen_level) || 0,
      didLevelUp: false,
    };

    if (
      this.props.gameLevelData &&
      this.props.gameLevelData.current_level > this.props.gameLevelData.last_seen_level
    ) {
      this.setState({
        fill: DEFAULT_FINAL_FILL,
      });
    }

    if (this.props.isLevelUpModal) {
      if (
        (this.state.currentLevel == this.currentLevel ||
          this.props.gameLevelData.isGrandFathered()) &&
        this.props.playConfetti
      ) {
        this.props.playConfetti();
      }
    }
  }

  componentDidUpdate(oldProps: ICircleProgressLevelProps, _oldState: ICircleProgressLevelState) {
    if (this.props.gameLevelData != oldProps.gameLevelData) {
      this.fillPercentage = this._createFillPercentageBasedOnUserLevelData();
      const newState = {
        currentLevel: (this.props.gameLevelData && this.props.gameLevelData.last_seen_level) || 0,
        fill: this.fillPercentage,
      };
      if (
        this.props.gameLevelData &&
        this.props.gameLevelData.current_level > this.props.gameLevelData.last_seen_level
      ) {
        newState.fill = DEFAULT_FINAL_FILL;
      }
      this.setState(newState);
    }
  }

  _shouldAnimate(): boolean {
    return (
      this.props.animated == true &&
      this.props.isLevelUpModal == true &&
      (this.state.didLevelUp || this.state.currentLevel == this.currentLevel)
    );
  }

  _createFillPercentageBasedOnUserLevelData(): number {
    if (!this.props.gameLevelData || !(this.props.gameLevelData instanceof SKZPlayerLevel)) {
      return 0;
    }

    return this.props.gameLevelData.getPercentageToNextLevel();
  }

  _incrementCurrentLevelText() {
    this.setState(
      {
        currentLevel: this.props.gameLevelData.isGrandFathered()
          ? this.currentLevel
          : this.state.currentLevel + 1,
        didLevelUp: true,
      },
      () => {
        if (
          this.state.currentLevel == this.currentLevel &&
          this.props.isLevelUpModal &&
          this.props.playConfetti
        ) {
          this.props.playConfetti();
        }
        setTimeout(() => {
          this.setState({
            didLevelUp: false,
          });
        }, BAR_DURATION);
      }
    );
  }

  _fillProgressCircle() {
    if (this.props.placeholder) {
      return;
    }
    this.setState({
      tintColor: SkillzStyle.fullStyles.tabHighlightColor.color,
      fill:
        this.state.currentLevel < this.props.gameLevelData.current_level
          ? DEFAULT_FINAL_FILL
          : this.fillPercentage,
    });
  }

  _updateProgressCircle() {
    if (this.state.fill >= DEFAULT_FINAL_FILL) {
      this._incrementCurrentLevelText();
      if (this.fillPercentage > 0) {
        this.setState(
          {
            tintColor: this.backgroundColor,
            fill: DEFAULT_INITIAL_PREFILL,
          },
          () => {
            setTimeout(() => {
              this._fillProgressCircle();
            }, SKZConstants.MS_PER_SEC); //if timeout is set to less than 1 sec, some levels will not animate the progress bar.
          }
        );
      }
    }
  }

  _styleOfProgressCircle(): Object {
    const getBackgroundColorInnerCircle = (): string => {
      if (this.props.backgroundColorInnerCircle) {
        return this.props.backgroundColorInnerCircle;
      } else if (this.props.isLevelUpModal) {
        return LIGHT_GRAY;
      } else {
        return this.backgroundColor;
      }
    };

    const getInnerCircleBorderColor = (): string => {
      if (this.props.innerCircleBorderColor) {
        return this.props.innerCircleBorderColor;
      } else if (this.props.isLevelUpModal) {
        return DARK_GRAY;
      } else {
        return addColors(this.backgroundColor, 'rgba(0,0,0,0.5)');
      }
    };

    const sizeOfInnerCircle = (this.size - PROGRESS_BAR_WIDTH * 2) * SIZE_INNER_CIRCLE;
    const borderRadiusOfInnerCircle = this.size / 2;

    return {
      backgroundColor: getBackgroundColorInnerCircle(),
      width: sizeOfInnerCircle,
      height: sizeOfInnerCircle,
      borderRadius: borderRadiusOfInnerCircle,
      borderWidth: 2,
      borderColor: getInnerCircleBorderColor(),
    };
  }

  _styleOfLevelCircleAnimatableView(): Object {
    const sizeOfCircle = this.size * SIZE_LEVEL_CIRCLE;
    const yOffset = -(this.size * Y_OFFSET);
    return {
      width: sizeOfCircle,
      height: sizeOfCircle,
      top: yOffset,
      paddingTop: -yOffset,
      marginBottom: yOffset,
    };
  }

  _styleOfLevelCircle(): Object {
    const sizeOfCircle = this.size * SIZE_LEVEL_CIRCLE;
    const borderRadiusOfInnerCircle = this.size / 2;
    const yOffset = -(this.size * Y_OFFSET);
    const color = this.props.placeholder
      ? this.backgroundColor
      : SkillzStyle.fullStyles.tabHighlightColor.color;
    return {
      width: sizeOfCircle,
      height: sizeOfCircle,
      borderRadius: borderRadiusOfInnerCircle,
      top: yOffset,
      marginBottom: yOffset,
      backgroundColor: color,
    };
  }

  _styleOfLevelCircleLabel(): Object {
    const sizeOfCircle = this.size * SIZE_LEVEL_CIRCLE;
    let color = SkillzStyle.fullStyles.btnTextColorOne.color;
    if (this.props.placeholder) {
      color = SkillzStyle.convertToOpacity(color, 0.4); // eslint-disable-line no-magic-numbers
    }

    if (this.state.currentLevel > 99) {
      if (Platform.OS === 'ios') {
        return {
          width: sizeOfCircle * SIZE_LEVEL_TEXT + 2,
          height: sizeOfCircle / 2 + 2,
          fontSize: sizeOfCircle * SIZE_LEVEL_TEXT - 2,
          color: color,
          backgroundColor: 'transparent',
        };
      }
      //android
      return {
        width: sizeOfCircle * SIZE_LEVEL_TEXT + 2,
        height: sizeOfCircle * SIZE_LEVEL_TEXT + 2,
        fontSize: sizeOfCircle * SIZE_LEVEL_TEXT - 6,
        color: color,
        backgroundColor: 'transparent',
      };
    }

    return {
      width: sizeOfCircle * SIZE_LEVEL_TEXT + 2,
      height: sizeOfCircle * SIZE_LEVEL_TEXT + 2,
      fontSize: sizeOfCircle * SIZE_LEVEL_TEXT - 2,
      color: color,
      backgroundColor: 'transparent',
    };
  }

  _renderInnerCircle() {
    return (
      <View style={[this._styleOfProgressCircle(), styles.innerCircle]}>
        {this.props.isNewTierDetails ? this._renderCurrentTierIcon() : this._renderIcon()}
      </View>
    );
  }

  _renderCurrentTierIcon(): React.ReactNode {
    return (
      <SkillzImage
        source={this.props.currentTierImage || ''}
        style={{ width: this.size, height: this.size, padding: 10 }}
        key={this.props.currentTierImage}
      />
    );
  }

  _renderIcon(): React.ReactNode {
    const sizeOfImage = this.size / 2;
    if (this.props.renderCenterImage) {
      return this.props.renderCenterImage(sizeOfImage, sizeOfImage);
    } else {
      return (
        <Optional test={!this.props.placeholder || this.props.gameLevelData}>
          <View style={styles.iconImg}>
            <SkillzImage
              source={this.props.gameLevelData.game_icon_url}
              style={[styles.iconImg, {width: sizeOfImage, height: sizeOfImage}]}
              key={this.props.gameLevelData.game_icon_url}
            />
          </View>
        </Optional>
      );
    }
  }

  _renderProgressCircle(): React.ReactNode {
    return (
      <AnimatedCircularProgressWithGradient
        size={this.size}
        width={PROGRESS_BAR_WIDTH}
        prefill={this.prefill}
        fill={this.props.isNewTierDetails ? this.props.tierProgress : this.state.fill}
        rotation={this.state.rotation}
        tintColor={this.state.tintColor}
        backgroundColor={this.backgroundColor}
        lineCap={this.props.placeholder ? 'butt' : 'round'}
        duration={this.props.isNewTierDetails ? BAR_DURATION_WITH_GRADIENT : BAR_DURATION}
        easing={Easing.bezier(.24, .42, .19, 1)}
        onAnimationComplete={(): void => this._updateProgressCircle()}
        isProgressWithGradient={this.props.isNewTierDetails}
        endColor={this.props.isNewTierDetails ? this.props.endColor : undefined}
        beginColor={this.props.isNewTierDetails ? this.props.beginColor : undefined}
      >
        {this._renderInnerCircle.bind(this)}
      </AnimatedCircularProgressWithGradient>
    );
  }

  _renderLevelCircleImage(): React.ReactNode {
    const mult = this.props.sizeOfImageMult ? this.props.sizeOfImageMult : 1;
    return (
      <View
        style={[this._styleOfLevelCircle(), styles.levelCircle]}
        testID={this.props.testId}
        accessibilityLabel={this.props.level?.name}
      >
        <SkillzImage
          style={{
            height: this.size * SIZE_LEVEL_CIRCLE * mult,
            width: this.size * SIZE_LEVEL_CIRCLE * mult,
          }}
          source={this.props.levelCircleImage}
          resizeMode={'contain'}
        />
      </View>
    );
  }

  _renderCurrentLevelText(): React.ReactNode {
    const currentLeveltext = (
      <Label
        style={[this._styleOfLevelCircleLabel(), styles.currentLevelText]}
        testID={this.props.levelTestID}
        adjustsFontSizeToFit={true}
        numberOfLines={1}
      >
        {this.state.currentLevel.toString()}
      </Label>
    );
    if (this._shouldAnimate()) {
      return (
        <Animatable.View
          animation={'fadeInUp'}
          duration={NUMBER_DURATION}
          useNativeDriver={true}
          easing={'ease-in-out'}
        >
          {currentLeveltext}
        </Animatable.View>
      );
    }
    return currentLeveltext;
  }

  _renderLevelCircle(): React.ReactNode {
    if (this.props.levelCircleImage) {
      return this._renderLevelCircleImage();
    }

    const levelCircle = (
      <View style={[this._styleOfLevelCircle(), styles.levelCircle]} testID={this.props.testId}>
        <Optional test={!this.props.placeholder || this.props.gameLevelData}>
          {this._renderCurrentLevelText()}
        </Optional>
      </View>
    );

    if (this._shouldAnimate() && !(Platform.OS === 'android')) {
      return (
        <Animatable.View
          animation={'pulse'}
          duration={NUMBER_DURATION}
          useNativeDriver={true}
          easing={'ease-in-out'}
          style={[this._styleOfLevelCircleAnimatableView(), styles.levelCircle]}
        >
          {levelCircle}
        </Animatable.View>
      );
    } else {
      return levelCircle;
    }
  }

  _renderTierPercentageText(): React.ReactNode {
    return (
      <View style={styles.tierPercentageTextContainer}>
      <Label style={styles.tierPercentageText}>{`${this.props.tierProgress}%`}</Label>
      </View>
    );
  }

  render(): React.ReactNode {
    return (
      <View style={styles.mainContainer}>
        {this._renderProgressCircle()}
        {this.props.isNewTierDetails ? this._renderTierPercentageText() : this._renderLevelCircle()}
      </View>
    );
  }
}
